import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { getMaterial, MATERIALS } from "@/data/materials";
import { getApplication } from "@/data/applications";
import { SITE, IMAGES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import NotFound from "@/pages/NotFound";

export default function MaterialPage() {
  const { slug } = useParams();
  const mat = getMaterial(slug);
  if (!mat) return <NotFound />;
  const related = MATERIALS.filter((m) => m.slug !== slug && m.category === mat.category)
    .concat(MATERIALS.filter((m) => m.slug !== slug && m.category !== mat.category))
    .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{mat.metaTitle}</title>
        <meta name="description" content={mat.metaDescription} />
      </Helmet>

      <div className="relative pt-44 pb-20 overflow-hidden" data-testid="material-hero">
        <img src={IMAGES.metalBars} alt={`${mat.name} spring material`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FEFEFE] via-[#FEFEFE]/88 to-white/50" />
        <div className="absolute inset-0 blueprint-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-mono-spec text-[11px] tracking-[0.3em] uppercase text-[#706F70]">
              <Link to="/materials" className="hover:text-[#334772] transition-colors">Materials</Link>
              <span className="mx-2 text-[#334772]">/</span>{mat.category}
            </p>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase leading-[0.92] text-[#334772] mt-6 max-w-4xl">
              {mat.title}
            </h1>
            <p className="mt-6 font-mono-spec text-sm tracking-[0.15em] uppercase text-[#334772]">{mat.tagline}</p>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <article className="lg:col-span-8">
          {mat.intro.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className={`text-[#706F70] leading-relaxed ${i === 0 ? "text-lg text-[#334772]" : "mt-5"}`}>{p}</p>
            </Reveal>
          ))}
          {mat.sections.map((s, i) => (
            <Reveal key={s.heading} className="mt-14">
              <div className="flex items-center gap-4 mb-4">
                <span className="font-mono-spec text-[#334772] text-sm tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                <span className="h-px w-10 bg-[#334772]" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-[#334772]">{s.heading}</h2>
              {s.body.map((p, j) => (
                <p key={j} className="mt-4 text-[#706F70] leading-relaxed">{p}</p>
              ))}
            </Reveal>
          ))}

          {mat.applications?.length > 0 && (
            <Reveal className="mt-16">
              <h2 className="font-display text-3xl uppercase tracking-wide text-[#334772]">Used in these applications</h2>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {mat.applications.map((as) => {
                  const a = getApplication(as);
                  return a ? (
                    <Link key={as} to={`/applications/${as}`} data-testid={`material-app-link-${as}`} className="group border border-[#334772]/15 p-5 hover:border-[#334772] transition-colors bg-[#FFFFFF]">
                      <span className="font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#9C9C9C]">Application</span>
                      <h3 className="font-display text-xl text-[#334772] uppercase tracking-wide mt-1 group-hover:text-[#706F70] transition-colors">{a.nav}</h3>
                    </Link>
                  ) : null;
                })}
              </div>
            </Reveal>
          )}
        </article>

        <aside className="lg:col-span-4 space-y-8">
          <Reveal>
            <div className="crosshair-card border border-[#334772]/15 bg-[#FFFFFF] p-7" data-testid="composition-table">
              <h3 className="font-mono-spec text-[11px] tracking-[0.3em] uppercase text-[#334772] mb-5">[ COMPOSITION ]</h3>
              <dl className="space-y-3">
                {mat.composition.map((c) => (
                  <div key={c.el} className="flex justify-between gap-4 border-b border-white/5 pb-3">
                    <dt className="font-mono-spec text-[11px] uppercase tracking-wider text-[#9C9C9C]">{c.el}</dt>
                    <dd className="font-mono-spec text-[11px] text-[#334772] text-right">{c.pct}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="border border-[#334772]/15 bg-[#FFFFFF] p-7" data-testid="properties-table">
              <h3 className="font-mono-spec text-[11px] tracking-[0.3em] uppercase text-[#334772] mb-5">[ SPRING PROPERTIES ]</h3>
              <dl className="space-y-3">
                {mat.properties.map((p) => (
                  <div key={p.label} className="flex justify-between gap-4 border-b border-white/5 pb-3">
                    <dt className="font-mono-spec text-[11px] uppercase tracking-wider text-[#9C9C9C]">{p.label}</dt>
                    <dd className="font-mono-spec text-[11px] text-[#334772] text-right">{p.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-[#334772]/40 bg-[#334772]/5 p-7">
              <h3 className="font-display text-2xl uppercase tracking-wide text-[#334772]">Need {mat.name} springs?</h3>
              <p className="mt-3 text-sm text-[#706F70] leading-relaxed">Wire in stock. Send your drawing or duty data for a 24-hour quotation.</p>
              <div className="mt-5 flex flex-col gap-3">
                <Link to="/enquiry" data-testid="material-rfq-btn" className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#334772] text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#2A3C60] transition-colors">
                  Send an RFQ <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
                <a href={SITE.mainSite} target="_blank" rel="noopener noreferrer" data-testid="material-mainsite-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-[#334772]/40 text-[#334772] text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#334772] hover:text-white transition-colors">
                  mescosprings.com <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </Reveal>
        </aside>
      </div>

      <div className="border-t border-[#334772]/15 bg-[#F3F5F8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <Reveal>
            <h2 className="font-display text-3xl uppercase tracking-wide text-[#334772] mb-8">Other spring materials</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r, i) => (
              <Reveal key={r.slug} delay={i * 0.08}>
                <Link to={`/materials/${r.slug}`} data-testid={`related-material-${r.slug}`} className="group block border border-[#334772]/15 p-6 hover:border-[#334772] hover:-translate-y-1 transition-all duration-300 bg-[#F3F5F8]">
                  <span className="font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#9C9C9C]">{r.category}</span>
                  <h3 className="font-display text-2xl text-[#334772] uppercase tracking-wide mt-2 group-hover:text-[#706F70] transition-colors">{r.name}</h3>
                  <p className="mt-2 text-sm text-[#706F70]">{r.tagline}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
